// Factory consumables: what is in stock, what needs ordering, and the rules.
//
// Mounted from AppMain when activeModule === 'consumables'.
//
// WHY STATUS AND "TO ORDER" ARE DIFFERENT COLUMNS
// -----------------------------------------------
// They answer different questions and will often disagree. An item can be
// perfectly healthy and still be below the level you like to keep: surgical
// gloves at 100 against a reorder point of 50 is not a problem, but it is
// 100 short of the 200 you want on the shelf. Collapsing the two into one
// "needs ordering" flag either cries wolf or misses the top-up.
//
// WHY A BLANK IS SHOWN WHERE AN ESTIMATE WOULD FIT
// ------------------------------------------------
// Days-remaining is blank until there is enough usage history to mean
// anything, with the reason on hover. A number somebody can plan around is
// worse than a blank when the number is a guess.

import React, { useCallback, useEffect, useState } from 'react';
import { authedFetch } from './utils/api';
import { color, radius, space } from './ui/theme';
import { Banner, Btn, Card, Chip, ErrorBox, SkeletonCards } from './ui/kit';

async function req(url, opts) {
  const res = await authedFetch(url, opts);
  if (!res.ok) {
    let d = `Request failed (${res.status})`;
    try {
      const j = await res.json();
      d = j.detail?.message || j.detail?.error || j.detail || d;
    } catch { /* keep */ }
    throw new Error(typeof d === 'string' ? d : 'Request failed');
  }
  return res.json();
}

const inputStyle = {
  width: '100%', padding: '7px 9px', borderRadius: radius.sm, fontSize: 13,
  border: `1px solid ${color.borderStrong}`, fontFamily: 'inherit',
  boxSizing: 'border-box', background: '#fff', color: color.text,
  textAlign: 'right',
};

const num = (v, dp = 0) =>
  v === null || v === undefined || v === ''
    ? '—'
    : Number(v).toLocaleString(undefined, { maximumFractionDigits: dp });

function th(align = 'left') {
  return {
    padding: space(1), textAlign: align, fontWeight: 600,
    borderBottom: `1px solid ${color.border}`, whiteSpace: 'nowrap',
  };
}
function td(align = 'left') {
  return {
    padding: space(1), textAlign: align,
    borderBottom: `1px solid ${color.border}`,
  };
}

const STATUS = {
  normal: { tone: 'success', label: 'Normal' },
  low_stock: { tone: 'warning', label: 'Low' },
  critical: { tone: 'danger', label: 'Critical' },
  out_of_stock: { tone: 'danger', label: 'Out of stock' },
};

// ---------------------------------------------------------------------------

function Thresholds({ item, onDone, onCancel, notify }) {
  const [critical, setCritical] = useState(String(item.critical_level ?? ''));
  const [reorder, setReorder] = useState(String(item.reorder_level ?? ''));
  const [target, setTarget] = useState(String(item.target_level ?? ''));
  const [lead, setLead] = useState(String(item.lead_time_days ?? ''));
  const [per, setPer] = useState(String(item.units_per_purchase_unit ?? ''));
  const [purchaseUnit, setPurchaseUnit] = useState(item.purchase_unit || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const n = (v) => (v === '' ? null : Number(v));
    setBusy(true);
    try {
      const r = await req(`/api/consumables/${item.raw_material_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_consumable: true,
          purchase_unit: purchaseUnit || null,
          units_per_purchase_unit: n(per),
          reorder_level: n(reorder),
          critical_level: n(critical),
          target_level: n(target),
          lead_time_days: n(lead),
        }),
      });
      notify?.(r.detail, 'success');
      onDone?.();
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <div style={{ fontWeight: 650, fontSize: 15 }}>{item.name}</div>
      <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: space(1.5) }}>
        Counted in {item.unit || 'units'} · {num(item.available)} available now
      </div>

      <Banner tone="info" title="What these three numbers mean">
        <strong>Critical</strong> is the point past which a late delivery
        stops production &mdash; roughly one lead time&apos;s cover.{' '}
        <strong>Reorder</strong> is when to start buying.{' '}
        <strong>Target</strong> is what a delivery should bring you back up
        to, and is what the suggested order is measured against.
      </Banner>

      <div style={{
        display: 'grid', gap: space(1.5), marginTop: space(1.5),
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      }}>
        {[['Critical', critical, setCritical],
          ['Reorder', reorder, setReorder],
          ['Target', target, setTarget],
          ['Lead time (days)', lead, setLead]].map(([label, value, setter]) => (
          <label key={label}>
            <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>
              {label}
            </div>
            <input style={inputStyle} value={value} inputMode="decimal"
                   onChange={(e) => setter(e.target.value)} />
          </label>
        ))}
      </div>

      <div style={{ marginTop: space(2), fontWeight: 650, fontSize: 14 }}>
        How it is bought
      </div>
      <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: space(1) }}>
        Stock is always held in {item.unit || 'the stock unit'}. This is only
        used to say how many to order &mdash; a box never enters the ledger.
      </div>
      <div style={{
        display: 'grid', gap: space(1.5),
        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
      }}>
        <label>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>
            Purchase unit
          </div>
          <input style={{ ...inputStyle, textAlign: 'left' }} value={purchaseUnit}
                 onChange={(e) => setPurchaseUnit(e.target.value)}
                 placeholder="e.g. box" />
        </label>
        <label>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>
            {item.unit || 'Units'} per {purchaseUnit || 'purchase unit'}
          </div>
          <input style={inputStyle} value={per} inputMode="decimal"
                 onChange={(e) => setPer(e.target.value)} placeholder="e.g. 50" />
        </label>
      </div>

      <div style={{ marginTop: space(2), display: 'flex', gap: space(1) }}>
        <Btn onClick={save} disabled={busy}>Save</Btn>
        <Btn variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Btn>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

function AddConsumable({ onDone, onCancel, notify }) {
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async (term) => {
    const data = await req(
      `/api/consumables/candidates?search=${encodeURIComponent(term)}`)
      .catch(() => []);
    setRows(data);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(query), 250);
    return () => clearTimeout(t);
  }, [query, load]);

  const mark = async (row) => {
    setBusy(row.id);
    try {
      const r = await req(`/api/consumables/${row.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_consumable: true }),
      });
      notify?.(r.detail, 'success');
      onDone?.();
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  return (
    <Card>
      <div style={{ fontWeight: 650, fontSize: 15, marginBottom: space(1) }}>
        Track an existing material as a consumable
      </div>
      <Banner tone="info" title="This does not create anything">
        It marks a material already in your inventory. If something is not
        registered at all, add it under Raw Materials first &mdash; a second
        place to register items is how two registers start disagreeing about
        what exists.
      </Banner>
      <div style={{ marginTop: space(1.5) }}>
        <input style={{ ...inputStyle, textAlign: 'left' }} value={query}
               onChange={(e) => setQuery(e.target.value)}
               placeholder="Search by name or code…" autoFocus />
      </div>
      <div style={{ marginTop: space(1.5), maxHeight: 360, overflowY: 'auto' }}>
        {rows.length === 0 && (
          <div style={{ fontSize: 13, color: color.textMuted, padding: space(1) }}>
            {query ? 'Nothing matches.' : 'Everything else is already tracked.'}
          </div>
        )}
        {rows.map((r) => (
          <div key={r.id} style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', gap: space(1), padding: space(1),
            borderBottom: `1px solid ${color.border}`,
          }}>
            <div style={{ fontSize: 13 }}>
              {r.name}
              <span style={{ color: color.textMuted }}>
                {r.sku ? ` · ${r.sku}` : ''}{r.unit ? ` · ${r.unit}` : ''}
                {r.category ? ` · ${r.category}` : ''}
              </span>
            </div>
            <Btn size="sm" onClick={() => mark(r)} disabled={busy === r.id}>
              Track
            </Btn>
          </div>
        ))}
      </div>
      <div style={{ marginTop: space(2) }}>
        <Btn variant="secondary" onClick={onCancel}>Done</Btn>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

// Raising a purchase request for what has run low.
//
// WHY ALREADY-ON-ORDER IS SUBTRACTED BEFORE ANYTHING IS SHOWN
// -----------------------------------------------------------
// Suggesting 420 pairs when 420 are already approved and coming is how a
// store ends up with 840. The quantity offered is what is still needed
// AFTER open requests, and an item fully covered is shown as such rather
// than silently dropped -- somebody looking for it should see why it is not
// in the list.
//
// A REQUEST IS NOT STOCK
// ----------------------
// Nothing on this screen changes a balance. Approving it does not either.
// Only a receipt does, and that happens in procurement.
function RaiseRequest({ onDone, onCancel, notify }) {
  const [rows, setRows] = useState(null);
  const [qty, setQty] = useState({});
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await req('/api/replenishment/suggestions');
        setRows(data);
        const seed = {};
        data.forEach((r) => {
          seed[r.raw_material_id] = r.still_to_order > 0
            ? String(r.still_to_order) : '';
        });
        setQty(seed);
      } catch (e) { setErr(e.message); }
    })();
  }, []);

  const send = async () => {
    const wanted = (rows || []).filter(
      (r) => Number(qty[r.raw_material_id] || 0) > 0);
    if (wanted.length === 0) {
      notify?.('Enter a quantity for at least one item', 'error');
      return;
    }
    const warehouseId = wanted[0].warehouse_id;
    if (!warehouseId) {
      notify?.('These items have no store on their stock record', 'error');
      return;
    }
    setBusy(true);
    try {
      const r = await req('/api/replenishment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          warehouse_id: warehouseId,
          lines: wanted.map((w) => ({
            raw_material_id: w.raw_material_id,
            quantity: Number(qty[w.raw_material_id]),
          })),
        }),
      });
      notify?.(r.detail, 'success');
      onDone?.();
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  if (err) return <ErrorBox msg={err} />;
  if (!rows) return <SkeletonCards n={2} />;

  if (rows.length === 0) {
    return (
      <Card>
        <Banner tone="success" title="Nothing needs ordering">
          Every tracked consumable is at or above its target level.
        </Banner>
        <div style={{ marginTop: space(2) }}>
          <Btn variant="secondary" onClick={onCancel}>Back</Btn>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ fontWeight: 650, fontSize: 15, marginBottom: space(1) }}>
        Raise a replenishment request
      </div>
      <Banner tone="info" title="This orders nothing and changes no stock">
        It raises a purchase request for approval. An approved request is a
        promise to buy, an order is a promise to deliver, and only a receipt
        is stock &mdash; so nothing below is added to any balance.
      </Banner>

      <div style={{ overflowX: 'auto', marginTop: space(1.5) }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: color.bg }}>
              <th style={th()}>Item</th>
              <th style={th()}>Unit</th>
              <th style={th('right')}>Available</th>
              <th style={th('right')}>Target</th>
              <th style={th('right')}>On order</th>
              <th style={th('right')}>Still needed</th>
              <th style={th('right')}>Request</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.raw_material_id}>
                <td style={td()}>
                  {r.name}
                  {r.covered && (
                    <span style={{ color: color.success, fontSize: 12 }}>
                      {' '}· already covered
                    </span>
                  )}
                </td>
                <td style={{ ...td(), color: color.textSecondary }}>{r.unit || '—'}</td>
                <td style={td('right')}>{num(r.available)}</td>
                <td style={{ ...td('right'), color: color.textMuted }}>{num(r.target_level)}</td>
                <td style={{ ...td('right'), color: r.on_order > 0 ? color.info : color.textMuted }}>
                  {r.on_order > 0 ? num(r.on_order) : '—'}
                </td>
                <td style={{ ...td('right'), fontWeight: 650 }}>
                  {num(r.still_to_order)}
                  {r.still_to_order_purchase_units ? (
                    <span style={{ color: color.textSecondary, fontWeight: 400 }}>
                      {' '}({num(r.still_to_order_purchase_units, 1)} {r.purchase_unit})
                    </span>
                  ) : null}
                </td>
                <td style={{ ...td('right'), width: 130 }}>
                  <input style={inputStyle} inputMode="decimal"
                         value={qty[r.raw_material_id] ?? ''}
                         onChange={(e) => setQty({
                           ...qty, [r.raw_material_id]: e.target.value })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: space(2), display: 'flex', gap: space(1) }}>
        <Btn onClick={send} disabled={busy}>Raise request</Btn>
        <Btn variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Btn>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

export default function ConsumablesDesk({ notify }) {
  const [rows, setRows] = useState(null);
  const [sum, setSum] = useState(null);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [raising, setRaising] = useState(false);

  const load = useCallback(async () => {
    setErr('');
    try {
      const [r, s] = await Promise.all([
        req('/api/consumables'),
        req('/api/consumables/summary').catch(() => null),
      ]);
      setRows(r); setSum(s);
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (err) return <ErrorBox msg={err} />;
  if (!rows) return <SkeletonCards n={4} />;

  if (editing) {
    return (
      <Thresholds item={editing} notify={notify}
                  onCancel={() => setEditing(null)}
                  onDone={() => { setEditing(null); load(); }} />
    );
  }

  if (raising) {
    return (
      <RaiseRequest notify={notify}
                    onCancel={() => setRaising(false)}
                    onDone={() => { setRaising(false); load(); }} />
    );
  }

  if (adding) {
    return (
      <AddConsumable notify={notify}
                     onCancel={() => setAdding(false)}
                     onDone={() => { setAdding(false); load(); }} />
    );
  }

  const shown = filter === 'all' ? rows
    : filter === 'attention' ? rows.filter((r) => r.needs_attention)
      : filter === 'to_order' ? rows.filter((r) => (r.suggested_order || 0) > 0)
        : rows.filter((r) => r.status === filter);

  const toOrder = rows.filter((r) => (r.suggested_order || 0) > 0);

  return (
    <div>
      {/* The cards come from the same query as the table, so they cannot
          disagree with the rows below them. */}
      {sum && (
        <div style={{
          display: 'grid', gap: space(1.5), marginBottom: space(2),
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        }}>
          {[['Tracked', sum.total, null],
            ['Need attention', sum.needs_attention,
              sum.needs_attention > 0 ? color.warning : null],
            ['Critical or out', (sum.by_status.critical || 0) + (sum.by_status.out_of_stock || 0),
              ((sum.by_status.critical || 0) + (sum.by_status.out_of_stock || 0)) > 0 ? color.danger : null],
            ['Below target', sum.suggested_orders, null]].map(([label, value, tone]) => (
            <Card key={label}>
              <div style={{ fontSize: 12.5, color: color.textSecondary }}>{label}</div>
              <div style={{ fontSize: 26, fontWeight: 650, color: tone || color.text }}>
                {value}
              </div>
            </Card>
          ))}
        </div>
      )}

      {rows.some((r) => r.status === 'out_of_stock') && (
        <div style={{ marginBottom: space(2) }}>
          <Banner tone="warning" title="Something has run out">
            Production that needs it will stop. Out of stock means nothing
            available &mdash; stock reserved for another job does not count.
          </Banner>
        </div>
      )}

      <div style={{
        display: 'flex', justifyContent: 'space-between', gap: space(1),
        flexWrap: 'wrap', marginBottom: space(2),
      }}>
        <div style={{ display: 'flex', gap: space(1), flexWrap: 'wrap' }}>
          {[['all', `All (${rows.length})`],
            ['attention', `Need attention (${rows.filter((r) => r.needs_attention).length})`],
            ['to_order', `Below target (${toOrder.length})`]].map(([k, label]) => (
            <Btn key={k} size="sm" variant={filter === k ? 'primary' : 'secondary'}
                 onClick={() => setFilter(k)}>{label}</Btn>
          ))}
        </div>
        <div style={{ display: 'flex', gap: space(1), flexWrap: 'wrap' }}>
          <Btn variant="secondary" onClick={() => setRaising(true)}>
            Raise replenishment request
          </Btn>
          <Btn onClick={() => setAdding(true)}>+ Track a material</Btn>
        </div>
      </div>

      <Card pad={0}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: color.bg }}>
                <th style={th()}>Item</th>
                <th style={th()}>Unit</th>
                <th style={th('right')}>On hand</th>
                <th style={th('right')}>Reserved</th>
                <th style={th('right')}>Available</th>
                <th style={th('right')}>Critical</th>
                <th style={th('right')}>Reorder</th>
                <th style={th('right')}>Target</th>
                <th style={th()}>Status</th>
                <th style={th('right')}>Days left</th>
                <th style={th('right')}>To order</th>
                <th style={th()}></th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td style={td()} colSpan={12}>
                  Nothing here. {filter !== 'all' && 'Try a different filter.'}
                </td></tr>
              )}
              {shown.map((r) => {
                const st = STATUS[r.status] || STATUS.normal;
                return (
                  <tr key={r.raw_material_id}>
                    <td style={td()}>{r.name}</td>
                    <td style={{ ...td(), color: color.textSecondary }}>{r.unit || '—'}</td>
                    <td style={td('right')}>{num(r.on_hand)}</td>
                    <td style={{ ...td('right'), color: color.textSecondary }}>
                      {r.reserved > 0 ? num(r.reserved) : '—'}
                    </td>
                    <td style={{ ...td('right'), fontWeight: 650 }}>{num(r.available)}</td>
                    <td style={{ ...td('right'), color: color.textMuted }}>{num(r.critical_level)}</td>
                    <td style={{ ...td('right'), color: color.textMuted }}>{num(r.reorder_level)}</td>
                    <td style={{ ...td('right'), color: color.textMuted }}>{num(r.target_level)}</td>
                    <td style={td()}><Chip tone={st.tone}>{st.label}</Chip></td>
                    <td style={{ ...td('right'), color: color.textSecondary }}
                        title={r.usage_basis || 'Not enough usage history yet'}>
                      {r.days_remaining === null || r.days_remaining === undefined
                        ? '—' : num(r.days_remaining)}
                    </td>
                    <td style={td('right')}>
                      {(r.suggested_order || 0) > 0 ? (
                        <span style={{ fontWeight: 600 }}>
                          {num(r.suggested_order)}
                          {r.suggested_order_purchase_units ? (
                            <span style={{ color: color.textSecondary, fontWeight: 400 }}>
                              {' '}({num(r.suggested_order_purchase_units, 1)} {r.purchase_unit})
                            </span>
                          ) : null}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={td()}>
                      <Btn size="sm" variant="secondary" onClick={() => setEditing(r)}>
                        Edit levels
                      </Btn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div style={{ marginTop: space(1.5), fontSize: 12, color: color.textMuted }}>
        Status and &ldquo;to order&rdquo; answer different questions and will
        often disagree. An item above its reorder point is not a problem, but
        it can still be short of what you like to keep on the shelf.
        &ldquo;Days left&rdquo; stays blank until there is enough recorded
        usage for an average to mean anything.
      </div>
    </div>
  );
}
